import { useState } from 'react';
import { Pencil, Loader2, X } from 'lucide-react';
import { supabase } from '../supabase/client';
import { useSelector, useDispatch } from 'react-redux';
import { setProfile } from '../Redux/store';

export default function ManualLocationButton({ onMessage }) {
  const dispatch = useDispatch();
  const userId = useSelector((s) => s.user.authUserId);
  const profile = useSelector((s) => s.user.profile);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const onSearch = async () => {
    if (!search.trim() || !userId) {
      if (onMessage) onMessage({ message: 'Please enter a location', type: 'error' });
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(search)}&limit=1&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'MakeBabies/1.0',
          },
        }
      );

      if (!res.ok) {
        throw new Error('Failed to search location');
      }

      const data = await res.json();

      if (!data || data.length === 0) {
        if (onMessage) onMessage({ message: 'Location not found. Try a more specific name.', type: 'error' });
        setLoading(false);
        return;
      }

      const result = data[0];
      const lat = parseFloat(result.lat);
      const lng = parseFloat(result.lon);
      const addr = result.address || {};

      const city = addr.city || addr.town || addr.village || addr.hamlet || '';
      const state = addr.state || addr.province || addr.region || '';
      const country = addr.country_code?.toUpperCase() || addr.country || '';

      const parts = [city, state, country].filter(Boolean);
      const display = parts.join(', ') || result.display_name.split(',')[0];

      const { error } = await supabase
        .from('profiles')
        .update({
          lat,
          lng,
          city,
          state,
          country,
          location: display,
        })
        .eq('id', userId);

      setLoading(false);

      if (error) {
        if (onMessage) onMessage({ message: error.message, type: 'error' });
        return;
      }

      dispatch(setProfile({ ...profile, lat, lng, city, state, country, location: display }));
      if (onMessage) onMessage({ message: `Location set: ${display}`, type: 'success' });
      setIsOpen(false);
      setSearch('');
    } catch (error) {
      setLoading(false);
      if (onMessage) onMessage({ message: 'Failed to search location', type: 'error' });
    }
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground"
        title="Set location manually"
        aria-label="Set location manually"
      >
        <Pencil className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Set Location Manually</h3>
          <button
            onClick={() => setIsOpen(false)}
            className="rounded-full p-1 hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Search for your location</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              placeholder="e.g., New York, NY or Paris, France"
              className="w-full rounded-md border border-input bg-background px-4 py-2 text-sm"
              autoFocus
            />
          </div>

          <button
            onClick={onSearch}
            disabled={loading || !search.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
            {loading ? 'Searching...' : 'Find Location'}
          </button>

          <p className="text-xs text-muted-foreground">
            Type your city and state/province. We'll find the coordinates and set your location.
          </p>
        </div>
      </div>
    </div>
  );
}
